import React, { Fragment, useState, useEffect, useRef } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import {
  Button,
  Container,
  Row,
  Col,
  Card,
  Alert,
  ListGroup,
  FormCheck,
  Spinner,
  Modal,
} from "react-bootstrap";
import { ArrowLeft, ShoppingCart } from "react-feather";

import { useAuth } from "../../contexts/authContext";
import { useCart } from "../../contexts/cartContext";
import { useConfirmation } from "../../contexts/confirmationContext";
import { useNotification } from "../../components/notification";
import apiService from "../../services/apiService";

import { loadScript } from "../../utils";
import { cartSession } from "../../utils/cart";

import CreateAccount from "./_createAccount";
import AddressForm from "../address/_form";
import CartSummary from "../cart/_summary";

const Checkout = () => {
  const navigate = useNavigate();
  const didFetch = useRef(false);
  const addressFormRef = useRef();
  const accountFormRef = useRef();

  // For this file, add:
  const location = useLocation();

  let { couponCode } = location.state || {};

  if (couponCode) {
    sessionStorage.setItem("couponCode", couponCode);
  } else {
    couponCode = sessionStorage.getItem("couponCode");
  }

  const { user, isAuthenticated } = useAuth();
  const { updateCartCount } = useCart();
  const { showNotification } = useNotification();
  const { showConfirmation } = useConfirmation();
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [loadingState, setLoadingState] = useState({ action: "" });
  const [cart, setCart] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [existingAddress, setExistingAddress] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [paymentError, setPaymentError] = useState(null);

  const redirectToLogin = () => {
    navigate("/login", { state: { from: "/checkout", couponCode } });
  };

  useEffect(() => {
    if (didFetch.current) return;
    didFetch.current = true;

    loadScript("https://checkout.razorpay.com/v1/checkout.js")
      .then(() => {})
      .catch((error) => {
        console.error("Failed to load Razorpay script", error);
      });

    const fetchData = async () => {
      try {
        let addressesData = [];

        if (isAuthenticated) {
          // Fetch addresses first
          addressesData = await apiService.get(`/addresses/user/${user.id}`);
          setAddresses(addressesData);
        }

        // Then fetch cart
        let apiURL = isAuthenticated
          ? `/cart/${cartSession.get("cartId")}`
          : `/cart?sessionId=${cartSession.get()}`;

        if (couponCode) {
          const joinChar = apiURL.includes("?") ? "&" : "?";
          apiURL += `${joinChar}couponCode=${encodeURIComponent(couponCode)}`;
        }

        const cartResponse = await apiService.get(apiURL);
        setCart(cartResponse);

        // Now handle address selection
        if (isAuthenticated && addressesData.length > 0) {
          let addressToSelect;

          // First try to use address from cart if available
          if (cartResponse.Order?.billingAddressId) {
            addressToSelect = addressesData.find(
              (addr) => addr.id === cartResponse.Order.billingAddressId
            );
          }

          // If no address from cart, use default or first address
          if (!addressToSelect) {
            addressToSelect =
              addressesData.find((addr) => addr.isDefault) || addressesData[0];
          }

          if (addressToSelect) setSelectedAddress(addressToSelect);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsPageLoading(false);
      }
    };

    fetchData();
  }, [isAuthenticated, user, couponCode]);

const handleAddressCreation = async () => {
  debugger;
  if (addressFormRef.current) {
    // Use the new isFormValid method
    const isValid = addressFormRef.current.isFormValid 
      ? addressFormRef.current.isFormValid() 
      : false;
    
    if (!isValid) {
      showNotification("danger", "Please verify your phone number before saving the address.", 5000);
      return;
    }
    
    await addressFormRef.current.submitForm();
  }
};

  const saveAddress = async (data) => {
    try {
      setLoadingState({ action: "save-address" });
      let response;
      if (existingAddress) {
        data.userId = existingAddress.userId;
        response = await apiService.put(
          `/addresses/${existingAddress.id}`,
          data
        );
      } else {
        data.userId = user.id;
        response = await apiService.post("/addresses", data);
      }
      if (response) {
        setAddresses((prev) =>
          existingAddress
            ? prev.map((address) =>
                address.id === existingAddress.id ? response : address
              )
            : [...prev, response]
        );
        showNotification("success", "Address saved!", 5000);
        resetForm();
      }
    } catch (error) {
      showNotification(
        "danger",
        error.message || "Failed to save address.",
        5000
      );
    } finally {
      setLoadingState(null);
    }
  };

  const handleAccountCreation = () => {
    if (accountFormRef.current) {
      accountFormRef.current.submitForm(); // Trigger form submission
    }
  };

  const createAccount = async (data) => {
    try {
      if(!accountFormRef.current.isFormValid()){
        return;
      }
      setLoadingState({ action: "create-account" });
      // Simulate API call to submit checkout data
      const response = await apiService.post("/auth/register", data);

      if (!response.emailVerified) {
        navigate("/confirm-email", {
          state: { email: data.email, from: "/checkout" },
        });
      }
    } catch (error) {
      showNotification("danger", error || "Checkout failed.", 5000);
    } finally {
      setLoadingState(null);
    }
  };

  const resetForm = () => {
    setExistingAddress(null); // Clear editing state
    setShowAddressForm(false); // Hide the form
  };

  const createOrder = () => {
    if (!selectedAddress) {
      showNotification("warning", "Please select an address to proceed.", 5000);
      return;
    }
    makePayment();
  };

  const toggleErrorModal = () => {
    const backdrop = document.querySelector(".modal-backdrop");
    if (backdrop) backdrop.style.zIndex = 9999999999;

    const modal = document.querySelector(".payment-error-modal");
    if (modal) modal.style.zIndex = 9999999999;
  };

  const makePayment = async () => {
    setLoadingState({ action: "make-payment" });
    try {
      // Step 1: Create an order on your backend
      const response = await apiService.post("/orders", {
        cartId: cart.id,
        shippingAddressId: selectedAddress.id,
        billingAddressId: selectedAddress.id,
      });
      const orderId = response.orderId;

      // Step 2: Open Razorpay checkout
      const options = {
        key: response.keyId,
        amount: cart.total * 100, // Amount in paise
        currency: "INR",
        name: process.env.REACT_APP_COMPANY_NAME,
        order_id: response.razorpayOrderId,
        handler: async (response) => {
          // Step 3: Verify payment on your backend
          try {
            await apiService.post("/payments/verify", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });

            navigate(`/payment-success/${orderId}`);
          } catch (verificationError) {
            rzp.close();
            toggleErrorModal();
            setPaymentError({
              title: "Verification Failed",
              description: `${verificationError}. Please try again later.`,
              paymentId: response.razorpay_payment_id,
              orderId: response.razorpay_order_id,
            });
            setShowErrorModal(true);
          }
        },
        prefill: {
          name: `${user.firstName} ${user.lastName}`,
          email: user.email,
          contact: user.phone,
        },
        theme: {
          color: "#3399cc",
        },
        modal: {
          // Handle when user closes the modal
          escape: false,
          handleback: false,
          confirm_close: true,
          ondismiss: () => {
            // showNotification(
            //   "warning",
            //   "Payment window was closed. Please complete your payment to place your order.",
            //   5000
            // );
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", function (response) {
        rzp.close();
        const error = response.error;
        toggleErrorModal();

        // Log the complete error for debugging
        // console.error("Payment failed with error:", error);

        // Map Razorpay error codes to user-friendly messages
        const errorMap = {
          // Standard Razorpay error codes
          BAD_REQUEST_ERROR: {
            title: "Bad Request Error",
            description: error.description,
          },
          PAYMENT_DECLINED: {
            title: "Payment Declined",
            description: error.description,
          },
          INSUFFICIENT_FUNDS: {
            title: "Insufficient Funds",
            description: error.description,
          },
          // Custom error reason mappings
          payment_failed: {
            title: "Payment Failed",
            description:
              "An unexpected error occurred. The payment processor declined the transaction. Please try again later.",
          },
          expired: {
            title: "Session Expired",
            description: "Your payment session expired. Please try again.",
          },
          default: {
            title: "Payment Error",
            description:
              "We encountered an issue processing your payment. Please try again or contact support.",
          },
        };

        let errorInfo = errorMap[error.code] || null;

        if (!errorInfo) {
          if (error.description.includes("expired"))
            errorInfo = errorMap.expired;
          else if (error.reason === "payment_failed")
            errorInfo = errorMap.payment_failed;
          else errorInfo = errorMap.default;
        }

        errorInfo.paymentId = error.metadata?.payment_id;
        errorInfo.orderId = error.metadata?.order_id;

        setPaymentError(errorInfo);
        setShowErrorModal(true);
      });
      rzp.open();
    } catch (error) {
      console.error("Payment failed", error);
    } finally {
      setLoadingState(null);
    }
  };

  // Handle Cancel Order
  const handleCancelConfirm = async () => {
    showConfirmation({
      title: "Confirm Order Cancellation",
      description: `Are you sure you want to cancel this order? This action is permanent and cannot be undone.`,
      confirmText: "Yes, Cancel",
      cancelText: "No, Keep It",
      variant: "primary",
      onConfirm: async () => {
        if (cart.Order?.orderNumber) handleCancelOrder();
        else clearCart();
      },
    });
  };

  const handleCancelOrder = async () => {
    setLoadingState({ action: "cancel-order" });
    try {
      await apiService.post(`/orders/${cart.Order.id}/cancel`);
      showNotification("success", "Order cancelled successfully!");
      cartSession.clear();
      // Update cart count whenever cart changes
      updateCartCount(0);
      navigate("/");
    } catch (error) {
      showNotification("danger", error?.message || "Failed to cancel order.");
    } finally {
      setLoadingState(null);
    }
  };

  const clearCart = async () => {
    setLoadingState({ action: "cancel-order" });
    try {
      const apiURL = isAuthenticated
        ? `/cart`
        : `/cart?sessionId=${cartSession.get()}`;
      await apiService.delete(apiURL);

      cartSession.clear();
      // Update cart count whenever cart changes
      updateCartCount(0);
      navigate("/");
    } catch (error) {
      showNotification(
        "danger",
        error.message || "Failed to remove cart.",
        5000
      );
    } finally {
      setLoadingState(null);
    }
  };

  if (
    !isPageLoading &&
    (!cart || Object.keys(cart).length === 0 || cart?.CartItems.length === 0)
  ) {
    return (
      <Row className="justify-content-center">
        <Col xs={"auto"} className="text-center">
          <Card className="mt-5 mb-4 p-5">
            <ShoppingCart size={32} className="text-light-emphasis m-auto" />
            <h5 className="mt-3">Your cart is empty</h5>
            <p className="text-muted mb-0">
              Looks like you haven't added any products to your cart yet.
            </p>
          </Card>
          <Link to="/" className="link-secondary">
            <ArrowLeft size={16} className="me-1" />
            BACK TO SHOP
          </Link>
        </Col>
      </Row>
    );
  }

  return (
    <Container className="p-0">
      {!isPageLoading &&
        cart &&
        Object.keys(cart).length > 0 &&
        cart?.CartItems.length > 0 && (
          <Row className="justify-content-center">
            <Col md={11} lg={8} xl={9}>
              {!isAuthenticated ? (
                <Fragment>
                  <Alert variant="light">
                    Already have an account?{" "}
                    <Button variant="link" onClick={redirectToLogin}>
                      Login here
                    </Button>
                  </Alert>
                  <CreateAccount
                    onSubmit={createAccount}
                    ref={accountFormRef}
                  />
                </Fragment>
              ) : (
                <Fragment>
                  <Card>
                    <Card.Body>
                      <h1 className="mb-2 fs-5">Checkout</h1>
                      <p className="mb-4 text-light-emphasis fw-light">
                        Please select your delivery address.
                      </p>
                      <h6 className="mb-3">Shipping Address</h6>
                      <ListGroup
                        className="mb-4"
                        style={{ maxHeight: "300px", overflowY: "auto" }}
                      >
                        {addresses.map((address) => (
                          <ListGroup.Item key={address.id}>
                            <div className="d-flex justify-content-between align-items-center">
                              <FormCheck
                                type="radio"
                                className="text-light-emphasis"
                                label={`${address.address1}, ${address.city}, ${address.country}`}
                                checked={selectedAddress?.id === address.id}
                                onChange={() => setSelectedAddress(address)}
                              />

                              <Button
                                variant="link"
                                onClick={() => {
                                  setExistingAddress(address);
                                  setShowAddressForm(true);
                                }}
                                className="ms-2 text-decoration-none link-secondary fw-semibold"
                              >
                                Edit
                              </Button>
                            </div>
                          </ListGroup.Item>
                        ))}
                      </ListGroup>

                      {!existingAddress && !showAddressForm && (
                        <Button
                          variant="primary"
                          className="btn-main"
                          onClick={() => setShowAddressForm(!showAddressForm)}
                        >
                          Add New Address
                        </Button>
                      )}

                      {showAddressForm && (
                        <Card>
                          <Card.Body>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <h6>
                                {existingAddress
                                  ? "Edit Address"
                                  : "Add New Address"}
                              </h6>
                              <Button
                                variant="link"
                                onClick={resetForm}
                                className="text-decoration-none link-secondary fw-semibold"
                              >
                                Cancel
                              </Button>
                            </div>
                            <AddressForm
                              onSubmit={saveAddress}
                              existingAddress={existingAddress}
                              ref={addressFormRef}
                            />
                            <div>
                              <Button
                                variant="secondary btn-main"
                                onClick={handleAddressCreation}
                                disabled={
                                  loadingState?.action === "save-address"
                                }
                              >
                                {loadingState?.action === "save-address" ? (
                                  <Spinner animation="border" role="status">
                                    <span className="visually-hidden">
                                      Loading...
                                    </span>
                                  </Spinner>
                                ) : (
                                  "Save Address"
                                )}
                              </Button>
                            </div>
                          </Card.Body>
                        </Card>
                      )}
                    </Card.Body>
                  </Card>
                </Fragment>
              )}
            </Col>
            <Col md={11} lg={4} xl={3} className="mt-4 mt-lg-0">
              {cart && (
                <Card className="bg-light p-3 rounded-3 border-0 mt-4 mt-lg-0">
                  <CartSummary cart={cart} />
                  {!isAuthenticated ? (
                    <Button
                      variant="primary"
                      className="w-100 mt-4 btn-main"
                      onClick={handleAccountCreation}
                      disabled={loadingState?.action === "create-account"}
                    >
                      {loadingState?.action === "create-account" ? (
                        <Spinner animation="border" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </Spinner>
                      ) : (
                        "CONTINUE"
                      )}
                    </Button>
                  ) : (
                    <Button
                      variant="primary"
                      className="w-100 mt-4 btn-main"
                      onClick={createOrder}
                      disabled={loadingState?.action === "make-payment"}
                    >
                      {loadingState?.action === "make-payment" ? (
                        <Spinner animation="border" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </Spinner>
                      ) : (
                        "PAY NOW"
                      )}
                    </Button>
                  )}
                  <Button
                    variant="dark"
                    className="w-100 mt-2 btn-main"
                    onClick={handleCancelConfirm}
                    disabled={loadingState?.action === "cancel-order"}
                  >
                    {loadingState?.action === "cancel-order" ? (
                      <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </Spinner>
                    ) : (
                      "CANCEL ORDER"
                    )}
                  </Button>
                </Card>
              )}
            </Col>
          </Row>
        )}
      <Modal
        show={isPageLoading}
        aria-labelledby="Loading"
        className="modal-loading"
        contentClassName="bg-transparent border-0"
        centered
      >
        <Modal.Body className="text-center">
          <Spinner animation="border" role="status" variant="primary">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </Modal.Body>
      </Modal>
      <Modal
        show={showErrorModal}
        backdrop="static"
        keyboard={false}
        centered
        size="md"
        contentClassName="p-4 border-0"
        className="payment-error-modal"
      >
        <Modal.Body>
          <h5>{paymentError?.title}</h5>
          <p className="mb-3 text-light-emphasis">
            {paymentError?.description} If you have any questions, please
            contact support with the following details:{" "}
          </p>
          {paymentError?.paymentId && (
            <p className="mb-1 text-dark-emphasis fs-medium">
              Payment ID:{" "}
              <span className="fw-medium">{paymentError.paymentId}</span>
            </p>
          )}
          {paymentError?.orderId && (
            <p className="mb-1 text-dark-emphasis fs-medium">
              Order ID:{" "}
              <span className="fw-medium">{paymentError.orderId}</span>
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="danger"
            className="btn-main"
            onClick={() => {
              setPaymentError(null);
              setShowErrorModal(false);
            }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default Checkout;
